import type { ViewState } from "../../types/warehouse.types";
import { CachedDimensions, Size, PlaceRendererConfig, PlaceColumn } from "../core/types";
import { PlaceLayoutCache } from "./PlaceLayoutCache";
import { PlaceSpriteFactory } from "./PlaceSpriteFactory";
import { PlaceStyleManager } from "./PlaceStyleManager";
import { PlaceLabelRenderer } from "./PlaceLabelRenderer";

/**
 * Handles the actual drawing of places
 *
 * Pattern: Strategy Pattern
 */
export class PlaceDrawer {
  constructor(
    private config: PlaceRendererConfig,
    private layoutCache: PlaceLayoutCache,
    private spriteFactory: PlaceSpriteFactory,
    private styleManager: PlaceStyleManager,
    private labelRenderer: PlaceLabelRenderer
  ) {}

  /**
   * Draw all columns
   */
  draw(
    ctx: CanvasRenderingContext2D,
    columns: PlaceColumn[],
    globalMaxLevel: number,
    viewState: ViewState,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    selectedPlaceIds: Set<string>,
    canvasSize?: Size
  ): void {
    const skipLabels = viewState.scale < 0.4;

    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      this.drawColumn(
        ctx,
        column,
        globalMaxLevel,
        viewState,
        worldToScreen,
        selectedPlaceIds,
        skipLabels,
        canvasSize
      );
    }
  }

  /**
   * Draw a single column
   */
  private drawColumn(
    ctx: CanvasRenderingContext2D,
    column: PlaceColumn,
    globalMaxLevel: number,
    viewState: ViewState,
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    selectedPlaceIds: Set<string>,
    skipLabels: boolean,
    canvasSize?: Size
  ): void {
    const basePlace = this.findBasePlace(column);
    if (!basePlace) return;

    const pos = worldToScreen(column.baseX, column.baseY);
    const dims = this.layoutCache.getDimensions(basePlace, viewState, this.config);

    const totalHeight =
      globalMaxLevel *
      (basePlace.placeDimensions.levelHeight * viewState.scale + dims.placeSpacing);
    const startY = pos.y - totalHeight / 2;

    // Draw each level in column (bottom to top)
    for (let level = 1; level <= column.maxLevel; level++) {
      const place = column.places[level];
      if (!place) continue;

      const levelY =
        startY +
        (globalMaxLevel - level) *
          (basePlace.placeDimensions.levelHeight * viewState.scale + dims.placeSpacing);
      const levelX = pos.x - dims.placeWidth / 2;

      // Get styles
      const colorSet = this.styleManager.getColorSet(place);
      const isSelected = selectedPlaceIds.has(place.place_id);
      const { color: strokeColor, width: strokeWidth } = this.styleManager.getStroke(
        place,
        isSelected,
        this.config
      );

      // Draw place
      this.drawPlace(ctx, levelX, levelY, dims, colorSet, strokeColor, strokeWidth);

      // Draw labels
      if (!skipLabels) {
        this.labelRenderer.render(ctx, place, level, levelX, levelY, dims, viewState);
      }

      // Store click geometry (subtract buffer offset if present)
      const bufferOffset = canvasSize?.bufferOffset || 0;
      place.clickGeometry = {
        levelX: levelX - bufferOffset,
        levelY: levelY - bufferOffset,
        placeWidth: dims.placeWidth,
        placeHeight: dims.placeHeight,
        isoDepthX: dims.isoDepthX,
        isoDepthY: dims.isoDepthY,
      };
    }
  }

  /**
   * Draw a single place using sprite
   */
  private drawPlace(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    dims: CachedDimensions,
    colorSet: any,
    stroke: string,
    lineWidth: number
  ): void {
    const sprite = this.spriteFactory.getSprite(
      dims.placeWidth,
      dims.placeHeight,
      dims.isoDepthX,
      dims.isoDepthY,
      colorSet,
      stroke,
      lineWidth
    );

    const padding = Math.ceil(lineWidth * 2);
    const offsetX = x + Math.min(0, dims.isoDepthX) - padding / 2;
    const offsetY = y + Math.min(0, dims.isoDepthY) - padding / 2;

    ctx.drawImage(sprite, offsetX, offsetY);
  }

  /**
   * Find base place in column
   */
  private findBasePlace(column: PlaceColumn) {
    for (let i = 1; i <= column.maxLevel; i++) {
      if (column.places[i]) return column.places[i];
    }
    return undefined;
  }
}
